/**
 * useDashboardDefault — DOMYŚLNY układ pulpitu tenanta (admin-only, Faza 23 Etap 2).
 *
 * Mirror `useDashboardLayout`, ale celuje w trasę zarządzania `/dashboard/default-layout`
 * (bramka `crm_core.dashboard.manage_default`). `GET` → `{ layout, templateName }` bieżącego
 * domyślnego lub oba `null` (brak → edytor startuje z wbudowanego `buildDefaultLayout`). `PUT`
 * upsertuje singleton. NIE myl z `useDashboardLayout` (per-user, self-only).
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

import type { DashboardLayoutDoc } from './types';

const QUERY_KEY = ['tenant', 'dashboard', 'default-layout'] as const;

interface DefaultLayoutResponse {
  layout: DashboardLayoutDoc | null;
  templateName: string | null;
}

export interface UseDashboardDefaultReturn {
  layout: DashboardLayoutDoc | null;
  templateName: string | null;
  isLoading: boolean;
  saveDefault: (layout: DashboardLayoutDoc, templateName?: string) => Promise<void>;
  isSaving: boolean;
}

export function useDashboardDefault(): UseDashboardDefaultReturn {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<DefaultLayoutResponse>('/dashboard/default-layout');
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minut
  });

  const saveMutation = useMutation({
    mutationFn: async (input: { layout: DashboardLayoutDoc; templateName?: string }) => {
      const res = await api.put('/dashboard/default-layout', input);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const saveDefault = useCallback(
    async (layout: DashboardLayoutDoc, templateName?: string) => {
      await saveMutation.mutateAsync({ layout, templateName });
    },
    [saveMutation],
  );

  return {
    layout: query.data?.layout ?? null,
    templateName: query.data?.templateName ?? null,
    isLoading: query.isLoading,
    saveDefault,
    isSaving: saveMutation.isPending,
  };
}
